import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, Autocomplete } from '../../../components/ui';
import SlugInput from './SlugInput';
import type { MicrositeType } from '../types/microsite.types';
import type { StoreBrandSearchResult, StoreSearchResult } from '../../../types/api.types';
import { searchStoreBrands, searchStores } from '../../../services/searchService';

interface BasicInfoSectionProps {
    micrositeType: MicrositeType;
    onMicrositeTypeChange: (type: MicrositeType) => void;
    storeBrandID: string;
    onStoreBrandIDChange: (id: string) => void;
    storeBrandSlug: string;
    onStoreBrandSlugChange: (slug: string) => void;
    storeBrandName?: string; // Display name for edit mode
    storeID?: string;
    onStoreIDChange: (id: string) => void;
    storeSlug?: string;
    onStoreSlugChange: (slug: string) => void;
    storeName?: string; // Display name for edit mode
    isEditMode?: boolean;
}

export default function BasicInfoSection({
    micrositeType,
    onMicrositeTypeChange,
    storeBrandID,
    onStoreBrandIDChange,
    storeBrandSlug,
    onStoreBrandSlugChange,
    storeBrandName,
    storeID,
    onStoreIDChange,
    storeSlug,
    onStoreSlugChange,
    storeName,
    isEditMode = false,
}: BasicInfoSectionProps) {
    const [selectedBrand, setSelectedBrand] = useState<StoreBrandSearchResult | null>(null);
    const [selectedStore, setSelectedStore] = useState<StoreSearchResult | null>(null);

    // Update selected brand when props change (for edit mode)
    useEffect(() => {
        if (isEditMode && storeBrandID && storeBrandName) {
            setSelectedBrand({
                _id: storeBrandID,
                name: storeBrandName,
            });
        }
    }, [isEditMode, storeBrandID, storeBrandName]);

    // Update selected store when props change (for edit mode)
    useEffect(() => {
        if (isEditMode && storeID && storeName && micrositeType === 'store') {
            setSelectedStore({
                _id: storeID,
                name: storeName,
            });
        }
    }, [isEditMode, storeID, storeName, micrositeType]);

    // Handle brand selection
    const handleBrandSelect = useCallback(
        (brand: StoreBrandSearchResult | null) => {
            setSelectedBrand(brand);

            if (brand) {
                onStoreBrandIDChange(brand._id);
            } else {
                onStoreBrandIDChange('');
                onStoreBrandSlugChange('');
            }

            // Reset store selection when brand changes
            setSelectedStore(null);
            onStoreIDChange('');
            onStoreSlugChange('');
        },
        [onStoreBrandIDChange, onStoreBrandSlugChange, onStoreIDChange, onStoreSlugChange]
    );

    // Handle store selection
    const handleStoreSelect = useCallback(
        (store: StoreSearchResult | null) => {
            setSelectedStore(store);

            if (store) {
                onStoreIDChange(store._id);
            } else {
                onStoreIDChange('');
                onStoreSlugChange('');
            }
        },
        [onStoreIDChange, onStoreSlugChange]
    );

    // Memoized search function for stores (requires selected brand)
    const handleStoreSearch = useCallback(
        async (searchText: string) => {
            if (!selectedBrand || !selectedBrand._id) {
                return [];
            }
            return searchStores(selectedBrand._id, searchText);
        },
        [selectedBrand]
    );

    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-[#e2e8f0]">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6e64f0] to-[#8b7cf6] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">1</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[#1e293b]">Basic Information</h3>
                        <p className="text-sm text-[#64748b]">
                            Choose the type of microsite and select the brand/store
                        </p>
                    </div>
                </div>

                {/* Microsite Type */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-[#1e293b]">
                        Microsite Type
                        <span className="text-[#ef4444] ml-1">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => !isEditMode && onMicrositeTypeChange('brand')}
                            disabled={isEditMode}
                            className={`p-4 rounded-lg border-2 transition-all text-left ${
                                micrositeType === 'brand'
                                    ? 'border-[#6e64f0] bg-[#f5f3ff]'
                                    : 'border-[#e2e8f0] hover:border-[#cbd5e1]'
                            } ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                                        micrositeType === 'brand'
                                            ? 'border-[#6e64f0] bg-[#6e64f0]'
                                            : 'border-[#cbd5e1]'
                                    }`}
                                >
                                    {micrositeType === 'brand' && (
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    )}
                                </div>
                                <div>
                                    <p className="font-semibold text-[#1e293b]">Brand Level</p>
                                    <p className="text-xs text-[#64748b] mt-1">
                                        Single microsite for entire brand
                                    </p>
                                </div>
                            </div>
                        </button>

                        <button
                            type="button"
                            onClick={() => !isEditMode && onMicrositeTypeChange('store')}
                            disabled={isEditMode}
                            className={`p-4 rounded-lg border-2 transition-all text-left ${
                                micrositeType === 'store'
                                    ? 'border-[#6e64f0] bg-[#f5f3ff]'
                                    : 'border-[#e2e8f0] hover:border-[#cbd5e1]'
                            } ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                                        micrositeType === 'store'
                                            ? 'border-[#6e64f0] bg-[#6e64f0]'
                                            : 'border-[#cbd5e1]'
                                    }`}
                                >
                                    {micrositeType === 'store' && (
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    )}
                                </div>
                                <div>
                                    <p className="font-semibold text-[#1e293b]">Store Specific</p>
                                    <p className="text-xs text-[#64748b] mt-1">
                                        Individual microsite for a store
                                    </p>
                                </div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Store Brand Selection - Two Column */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Autocomplete<StoreBrandSearchResult>
                        label="Store Brand"
                        placeholder="Search for a brand..."
                        value={selectedBrand}
                        onSelect={handleBrandSelect}
                        onSearch={searchStoreBrands}
                        displayField="name"
                        idField="_id"
                        required
                        disabled={isEditMode}
                    />

                    {/* Store Brand Slug */}
                    <SlugInput
                        label="Store Brand Slug"
                        value={storeBrandSlug}
                        onChange={onStoreBrandSlugChange}
                        sourceText={selectedBrand?.name}
                        placeholder="brand-slug"
                        required
                    />
                </div>

                {/* Store Selection (only for store-specific) - Two Column */}
                {micrositeType === 'store' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Autocomplete<StoreSearchResult>
                            label="Store"
                            placeholder={
                                selectedBrand ? 'Search for a store...' : 'Select a brand first...'
                            }
                            value={selectedStore}
                            onSelect={handleStoreSelect}
                            onSearch={handleStoreSearch}
                            displayField="name"
                            idField="_id"
                            disabled={!selectedBrand || isEditMode}
                            required
                        />

                        <SlugInput
                            label="Store Slug"
                            value={storeSlug || ''}
                            onChange={onStoreSlugChange}
                            sourceText={selectedStore?.name}
                            placeholder="store-slug"
                            required
                            disabled={!storeID}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
